import { Attachment } from '@shared/interfaces/attachment.interface';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  input,
  inject
} from '@angular/core';
import { ICommentaryEvent } from '../../interface/commentary-event.interface';
import { CommentaryEventType } from '../../enums/commentary-event-type.enum';
import { Commentary } from '../../models/commentary.model';
import { Employee } from '@shared/models/employee.model';
import { publicFile } from '@shared/helpers/public-file.helper';
import { RemoveModalController } from '@shared/controllers/remove-modal.controller';
import { ButtonTypes, ButtonSize } from '@shared/modules/ui/components/button/button.component';
import { TextAttachmentsInputComponent } from '@shared/components/text-attachments-input/text-attachments-input.component';
import { TextAttachmentsConfig } from '@shared/components/text-attachments-input/models/text-attachments-config';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { GbxsoftInputTypes } from '@form/src/lib/gbxsoft-input/gbxsoft-input.types';
import { GbxsoftInputConfig } from '@form/src/lib/gbxsoft-input/interfaces/gbxsoft-input.interface';
import { TranslateService } from '@ngx-translate/core';
import { TextAttachment } from '@shared/components/text-attachments-input/models/text-attachment.model';
import { Config } from '@shared/configs/config';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDiscardModalComponent } from '@shared/components/confirmation-discard-modal/confirmation-discard-modal.component';
import {
  AttachmentLibraryDialogResult,
  AttachmentLibraryModalComponent
} from '@shared/modules/attachment-manage/components/attachment-library-modal/attachment-library-modal.component';
import { AttachmentsManageService } from '@shared/modules/attachment-manage/services/attachments-manage.service';
import { AttachmentPacket } from '@shared/modules/attachment-manage/models/attachment-packet.model';

@Component({
  selector: 'commentary-item-v2',
  templateUrl: './commentary-item-v2.component.html',
  styleUrls: ['./commentary-item-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AttachmentsManageService]
})
export class CommentaryItemV2Component {
  ButtonTypes = ButtonTypes;
  ButtonSize = ButtonSize;
  Config = Config;
  readonly projectId = input<number | null>(null);

  attachments: Array<TextAttachment> = [];
  attachmentsToDelete: Array<number> = [];
  isEditMode = false;
  private sourceAttachmentIds: number[] = [];
  private readonly attachmentDialog = inject(MatDialog);

  form: UntypedFormGroup = this.fb.group({
    note: [null],
    files: [null]
  });

  config: GbxsoftInputConfig = {
    name: ' ',
    type: GbxsoftInputTypes.TEXT,
    placeholder: this.t.instant('Commentaries.addCommentary'),
    showPlaceholderOnFocus: true
  };

  get formData() {
    const files = this.form.value['files']?.filter((i) => !i.created);

    let _data = {
      content: this.form.get('note').value || '',
      attachments: files ? files : [],
      attachmentPacketId: this.attachmentsManageService.attachmentPacket?.id || null,
      attachmentsToDelete: this.attachmentsToDelete
    };

    return _data;
  }

  @ViewChild('textAttach') textAttach: TextAttachmentsInputComponent;

  @Input() maxImagesToShow: number = 3;
  @Input() commentary: Commentary = null;
  @Input() attachmentsConfig: TextAttachmentsConfig = {
    photoAttachments: true,
    fileAttachments: true,
    showText: true
  };

  @Output() commentaryEvent: EventEmitter<ICommentaryEvent> = new EventEmitter();
  @Output() commentaryEdited: EventEmitter<any> = new EventEmitter();

  constructor(
    private changes: ChangeDetectorRef,
    private fb: UntypedFormBuilder,
    private t: TranslateService,
    private dialog: MatDialog,
    private attachmentsManageService: AttachmentsManageService
  ) {}

  getPhoto(employee: Employee) {
    return employee?.userPerson?.photo ? publicFile(employee.userPerson.photo) : null;
  }

  edit() {
    this.isEditMode = true;
    this.changes.detectChanges();

    setTimeout(() => {
      this.attachments = this.commentary['attachments']?.map((attachment: Attachment) =>
        this.textAttach.getAttachment(attachment as any, null, attachment)
      );
      this.form.patchValue({ note: this.commentary.content });
      this.form.markAsPristine();
      this.textAttach.setFilesToFormControl();
      this.changes.detectChanges();
    });
  }

  remove() {
    const ctrl = new RemoveModalController();
    ctrl
      .remove(
        () => {
          this.commentaryEvent.emit({ type: CommentaryEventType.REMOVE, data: this.commentary });
          this.changes.detectChanges();
        },
        () => {},
        {
          title: 'Commentaries.V2.removeEitle',
          body: 'Commentaries.V2.removeDescription',
          submitButtonText: 'Commentaries.V2.removeConfirm'
        }
      )
      .subscribe();
  }

  removeFileEvent(attachment: TextAttachment) {
    if (attachment.sourceAttachmentId) {
      this.sourceAttachmentIds = this.sourceAttachmentIds.filter((id) => id !== attachment.sourceAttachmentId);
      this.rebuildSourcePacket();
      return;
    }
    attachment.id ? this.attachmentsToDelete.push(parseInt(attachment.id)) : null;
    this.changes.detectChanges();
  }

  cancelEdit() {
    if (this.form.pristine) {
      this.isEditMode = false;
      this.attachments = [];
      this.attachmentsToDelete = [];
    } else {
      let dialogRef = this.dialog.open(ConfirmationDiscardModalComponent, {
        width: Config.DEFAULT_MODAL_WIDTH,
        autoFocus: false
      });

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        switch (confirmed) {
          case true:
            {
              this.isEditMode = false;
              this.attachments = [];
              this.attachmentsToDelete = [];
              this.changes.detectChanges();
            }
            break;
          default:
            break;
        }
      });
    }
  }

  saveEditedComment() {
    this.commentaryEdited.emit(this.formData);
    this.isEditMode = false;
    this.attachments = [];
    this.attachmentsToDelete = [];
  }

  openAttachmentLibrary(): void {
    const projectId = this.projectId();
    if (!projectId) return;

    const dialogRef = this.attachmentDialog.open(AttachmentLibraryModalComponent, {
      width: '1120px',
      height: 'calc(100vh - 96px)',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: '760px',
      autoFocus: false,
      disableClose: true,
      data: { projectId, attachmentsConfig: this.attachmentsConfig, attachedIds: this.sourceAttachmentIds, maxSelectable: null }
    });
    dialogRef.afterClosed().subscribe((result?: AttachmentLibraryDialogResult) => {
      if (!result?.sourceAttachmentIds?.length) return;
      this.addSourceAttachments(projectId, result.sourceAttachmentIds);
    });
  }

  private addSourceAttachments(projectId: number, ids: number[], rebuild = false): void {
    const packetId = this.attachmentsManageService.attachmentPacket?.id;
    const request$ = packetId
      ? this.attachmentsManageService.updateAttachmentPacketFromSources(packetId, projectId, ids)
      : this.attachmentsManageService.createAttachmentPacketFromSources(projectId, ids);

    request$.subscribe({
      next: (response) => {
        const packet = response.result;
        if (!packet) return;
        const addedIds = rebuild ? this.sourceAttachmentIds : ids.filter((id) => !this.sourceAttachmentIds.includes(id));
        this.sourceAttachmentIds = [...new Set([...this.sourceAttachmentIds, ...ids])];
        if (rebuild) this.attachments = this.attachments.filter((item) => !item.sourceAttachmentId);
        this.addPacketAttachments(packet, addedIds);
        this.textAttach.setFilesToFormControl();
        this.changes.detectChanges();
      }
    });
  }

  private rebuildSourcePacket(): void {
    const packetId = this.attachmentsManageService.attachmentPacket?.id;
    if (!packetId) return;
    this.attachmentsManageService.deleteAttachmentPacket(packetId).subscribe({
      next: () => {
        this.attachmentsManageService.setInitialAttachmentPacket();
        if (this.sourceAttachmentIds.length) this.addSourceAttachments(this.projectId(), this.sourceAttachmentIds, true);
      }
    });
  }

  private addPacketAttachments(packet: AttachmentPacket, ids: number[]): void {
    const attachments = (packet.attachments || []).slice(-ids.length);
    ids.forEach((sourceAttachmentId, index) => {
      const attachment = attachments[index];
      if (!attachment) return;
      this.attachments.push(
        new TextAttachment({
          id: attachment.id?.toString(),
          name: attachment.name,
          file: attachment,
          isUploaded: true,
          created: attachment.created || 'packet',
          sourceAttachmentId
        })
      );
    });
  }

  trackAttachment(index: number, attachment: Attachment) {
    return attachment.id;
  }
}
